<template>
   <LoadingPage v-if="isLoading"></LoadingPage>
   
   <!-- ── Page Header ── -->
   <div class="ms-page-header">
      <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
         <div class="d-flex align-items-center gap-3">
            <div class="ms-page-header__icon">
               <i class="fas fa-users"></i>
            </div>
            <div>
               <p class="ms-page-header__breadcrumb">Platform Settings <span class="ms-breadcrumb-sep">/</span> Vertex BDM Details</p>
               <h1 class="ms-page-header__title">Vertex BDM Details</h1>
            </div>
         </div>
         <div v-if="defaultBdm" class="default-bdm-pill ms-auto">
            <i class="fas fa-star text-success me-2"></i>
            <span>Default BDM for New Leads: <strong>{{ defaultBdm.first_name }} {{ defaultBdm.last_name }}</strong> <span v-if="defaultBdm.email">({{ defaultBdm.email }})</span></span>
         </div>
      </div>
   </div>

   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">
            <div class="col-12 col-lg-3 mb-2 mb-lg-0">
               <input v-model.trim="search" type="text" class="form-control form-control-sm"
                  placeholder="🔍 Search..."
                  style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif; font-weight: 400;"
                  @keyup="onSearch()" />
            </div>

             <div v-if="$store.getters['auth/isGlobalSuperAdmin']" class="col-12 col-lg-auto d-flex mb-2 mb-lg-0">
                <button type="button" class="btn-primary-gradient px-4"
                   style="font-size: 14px; height: 40px; display: inline-flex; align-items: center; gap: 8px;"
                   @click="openAddModal()">
                   <i class="fas fa-plus"></i> Add Vertex BDM
                </button>
             </div>

            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">
               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort By:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="sortOption in sortByList" :value="sortOption.value" :key="sortOption.value">
                        {{ sortOption.text }}
                     </option>
                  </select>
               </div>
               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Rows per page:</span>
                  <select v-model="pagination.pageSize" class="form-select form-select-sm shadow-none filterOptions bordered-select">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}
                     </option>
                  </select>
               </div>

               <div class="pagination">
                  <button @click="previousPage()" :disabled="pagination.page === 1" class="pagination-arrow">
                     <i class="fas fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()" :disabled="pagination.page === pagination.totalPages" class="pagination-arrow">
                     <i class="fas fa-chevron-right"></i>
                  </button>
               </div>
            </div>
         </div>
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col" v-for="header in filteredHeaders" :key="header.value">{{ header.text }}</th>
                  </tr>
               </thead>
               <tbody v-if="!bdmList.length">
                  <tr>
                     <td :colspan="filteredHeaders.length">No records to display.</td>
                  </tr>
               </tbody>
                <tbody v-else>
                   <tr v-for="(bdm) in bdmList" :key="bdm.id" :class="{ 'row-deleted': isUserDeleted(bdm) || bdm.auth_user_id === 0 }">
                        <td :class="{ 'text-muted': isUserDeleted(bdm) || bdm.auth_user_id === 0 }">
                           <template v-if="bdm.auth_user_id === 0">
                              <span class="badge-orphaned">
                                 <i class="fas fa-exclamation-triangle me-1"></i>Orphaned Record
                              </span>
                           </template>
                           <template v-else-if="isUserDeleted(bdm)">
                              <span class="badge-deleted">
                                 <i class="fas fa-user-slash me-1"></i>Deleted User
                              </span>
                           </template>
                           <template v-else>
                              {{ bdm.last_name || '—' }}
                           </template>
                        </td>
                        <td :class="{ 'text-muted': isUserDeleted(bdm) || bdm.auth_user_id === 0 }">
                           <template v-if="bdm.auth_user_id === 0">
                              <span class="badge-orphaned">
                                 <i class="fas fa-exclamation-triangle me-1"></i>Orphaned Record
                              </span>
                           </template>
                           <template v-else-if="isUserDeleted(bdm)">
                              <span class="badge-deleted">
                                 <i class="fas fa-user-slash me-1"></i>Deleted User
                              </span>
                           </template>
                           <template v-else>
                              {{ bdm.first_name || '—' }}
                              <span v-if="bdm.is_default" class="badge-default-bdm ms-2">
                                 <i class="fas fa-star me-1"></i>Default
                              </span>
                           </template>
                        </td>
                      <td :class="{ 'text-muted': !bdm.mobile }">{{ bdm.mobile || '—' }}</td>
                      <td :class="{ 'text-muted': !bdm.email }">{{ bdm.email || '—' }}</td>
                      <td>
                         <a v-if="bdm.calendar_link" :href="bdm.calendar_link" target="_blank" :title="bdm.calendar_link">{{ truncateLink(bdm.calendar_link) }}</a>
                         <span v-else class="text-muted">—</span>
                      </td>
                      <td v-if="$store.getters['auth/isGlobalSuperAdmin']" class="action-cell">
                         <button type="button" class="action-btn-circle ms-1 me-1"
                            :class="bdm.is_default ? 'action-btn-circle--default-active' : 'action-btn-circle--default'"
                            :title="bdm.is_default ? 'Current Default BDM' : 'Set as Default BDM'"
                            @click="setDefaultBdm(bdm)">
                            <i :class="bdm.is_default ? 'fas fa-star' : 'far fa-star'"></i>
                         </button>
                         <button type="button" class="action-btn-circle action-btn-circle--edit ms-1 me-1"
                            title="Click to edit BDM" @click="openEditModal(bdm)">
                            <i class="fas fa-pencil-alt"></i>
                         </button>
                         <button type="button" class="action-btn-circle action-btn-circle--delete ms-1" title="Click to delete"
                            @click="deleteBdm(bdm)">
                            <i class="fas fa-trash-alt"></i>
                         </button>
                      </td>
                   </tr>
                </tbody>
            </table>
         </div>
      </div>
   </div>

   <div v-if="isModalOpen" class="modal-backdrop-custom">
      <div class="modal-content-custom">
         <button @click="isModalOpen = false" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">{{ isEditing ? 'Edit' : 'Add' }} Vertex BDM</h5>
         <hr>
         <form @submit.prevent="saveBdm" class="modal-form-custom">
            <div class="mb-3">
               <label for="vertexUser" class="form-label">Select Vertex User <span style="color: red;">*</span></label>
               <select id="vertexUser" class="form-select" v-model="currentBdm.userId" :disabled="isEditing" required>
                  <option disabled value="">- Select Vertex User -</option>
                  <option v-for="user in vertexUsers" :key="user.id" :value="user.id">
                     {{ user.fullName }}{{ user.email ? ` (${user.email})` : '' }}
                  </option>
               </select>
            </div>
            
            <div class="mb-3">
               <label for="bdmMobile" class="form-label">BDM Mobile <span style="color: red;">*</span></label>
               <div class="input-group">
                  <div class="custom-dropdown" ref="dropdown">
                     <div class="dropdown-selected" @click="toggleDropdown">
                        <span class="me-2">{{ selectedCountry.flag }}</span>
                        <span>{{ selectedCountry.code }}</span>
                     </div>
                     <ul v-if="isDropdownOpen" class="dropdown-list">
                        <li v-for="country in countryCodes" :key="country.iso" @click="selectCountry(country)">
                           <span class="me-3">{{ country.flag }}</span>
                           <span>{{ country.name }} ({{ country.code }})</span>
                        </li>
                     </ul>
                  </div>
                  <input type="tel" id="bdmMobile" class="form-control" v-model.trim="currentBdm.localMobile"
                     placeholder="412345678" required>
               </div>
            </div>
            <div class="mb-3">
               <label for="bdmEmail" class="form-label">BDM Email <span style="color: red;">*</span></label>
               <input type="email" id="bdmEmail" class="form-control" v-model.trim="currentBdm.email"
                  placeholder="bdms@vtxsecurity.com" required>
            </div>
            <div class="mb-3">
               <label for="bdmCalendlyLink" class="form-label">BDM Calendly Link <span
                     style="color: red;">*</span></label>
               <input type="url" id="bdmCalendlyLink" class="form-control" v-model.trim="currentBdm.calendlyLink"
                  placeholder="https://calendly.com/your-link" required>
            </div>
            <div class="mt-4 text-center">
               <button type="submit" class="btn-primary-gradient add-button-custom">{{ isEditing ? 'Update' : 'Add'
                  }}</button>
            </div>
         </form>
      </div>
   </div>

   <ConfirmModal
      v-model="showConfirmModal"
      :title="confirmModalTitle"
      :message="confirmModalMessage"
      @confirm="onConfirmAction"
   />
</template>

<script>
import { debounce, sortAlphabetically } from '@/plugins/helper.js';
import LoadingPage from '@/components/Utilities/vertex-loading.vue';

// NO MORE EXTERNAL IMPORTS FOR THE DROPDOWN
import { countryCodes } from './countryCodes.js';

export default {
   components: {
      LoadingPage,
   },
   data() {
      return {
         // ... (other data properties are the same)
         isDropdownOpen: false, // NEW: State for our manual dropdown
         isLoading: false,
         isModalOpen: false,
         showConfirmModal: false,
         confirmModalTitle: '',
         confirmModalMessage: '',
         pendingDeleteId: null,
         isEditing: false,
         search: "",
         headers: [
            { text: "Last Name", value: "bdm_last_name" },
            { text: "First Name", value: "bdm_first_name" },
            { text: "BDM Mobile", value: "bdm_mobile" },
            { text: "BDM Email", value: "bdm_email" },
            { text: "Calendly Link", value: "bdm_calendly_link" },
            { text: "Action", value: "action" },
         ],
         allBdms: [],
         bdmList: [],
         allVertexUsers: [],
         vertexUsers: [],
         countryCodes: countryCodes,
         currentBdm: {
            id: null,
            userId: "",
            countryCode: '+61', // Default to Australia's code
            localMobile: "",
            email: "",
            calendlyLink: "",
            onBehalfOfUserId: "",
         },
         pagination: {
            pageSize: 5,
            page: 1,
            totalItems: 0,
            totalPages: 1,
         },
         rowsPerPageList: [{ text: "5", value: 5 }, { text: "10", value: 10 }, { text: "20", value: 20 }],
         sortByList: [
            { text: "Newest", value: "date_desc" },
            { text: "Oldest", value: "date_asc" },
            { text: "Name (A-Z)", value: "name_asc" }
         ],
         sortBy: "date_desc",
         debounceFn: null,
      };
   },
   // NEW COMPUTED PROPERTY to easily find the selected country object
   computed: {
       selectedCountry() {
          return this.countryCodes.find(c => c.code === this.currentBdm.countryCode) || this.countryCodes.find(c => c.iso === 'AU');
       },
       defaultBdm() {
          if (!this.allBdms) return null;
          return this.allBdms.find(b => b.is_default);
       },
       paginationLabel() {
          const start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems);
          const end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems);
          return `${start} - ${end}`;
       },
       availableOnBehalfOfBdms() {
          // Filter out the current BDM, orphaned records, and deleted users from the list of all BDMs,
          // but keep them if they are currently selected on the BDM being edited.
          return this.allBdms.filter(bdm => {
             const isSelf = bdm.id === this.currentBdm.id || bdm.auth_user_id === this.currentBdm.userId;
             const isOrphaned = bdm.auth_user_id === 0 || this.isUserDeleted(bdm);
             const isCurrentlySelected = bdm.auth_user_id && String(bdm.auth_user_id) === String(this.currentBdm.onBehalfOfUserId);
             return !isSelf && (!isOrphaned || isCurrentlySelected);
          });
       },
       filteredHeaders() {
          if (!this.$store.getters['auth/isGlobalSuperAdmin']) {
             return this.headers.filter(h => h.value !== 'action');
          }
          return this.headers;
       },
       filteredOnBehalfOfUsers() {
          if (!this.allVertexUsers) return [];
          return this.allVertexUsers.filter(user => user.id !== this.currentBdm.userId);
       },
   },
   // NEW LIFECYCLE HOOKS to handle closing the dropdown when clicking outside
   mounted() {
      document.addEventListener('click', this.handleClickOutside);
   },
   beforeUnmount() {
      document.removeEventListener('click', this.handleClickOutside);
   },
   created() {
      this.init();
      this.debounceFn = debounce(() => this.processClientSideData(), 500);
   },
   watch: {
      'pagination.pageSize'() { this.pagination.page = 1; this.processClientSideData(); },
      sortBy() { this.processClientSideData(); },
      'pagination.page'() { this.processClientSideData(); },
      'currentBdm.userId'(newVal) {
          if (newVal && newVal === this.currentBdm.onBehalfOfUserId) {
             this.currentBdm.onBehalfOfUserId = "";
          }
      }
   },
   methods: {
      truncateLink(url, startLength = 25, endLength = 15) {
         // If the URL is empty or short enough, return it as is.
         if (!url || url.length < (startLength + endLength + 3)) {
            return url;
         }
         
         // Get the beginning part of the URL
         const start = url.substring(0, startLength);
         
         // Get the ending part of the URL
         const end = url.substring(url.length - endLength);
         
         // Combine them with "..." in the middle
         return `${start}...${end}`;
      },
      // NEW METHODS for our manual dropdown
      toggleDropdown() {
         this.isDropdownOpen = !this.isDropdownOpen;
      },
      selectCountry(country) {
         this.currentBdm.countryCode = country.code;
         this.isDropdownOpen = false;
      },
      handleClickOutside(event) {
         if (this.$refs.dropdown && !this.isDropdownOpen) {
            return
         }
         if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
            this.isDropdownOpen = false;
         }
      },
      // ... (All other methods like init, getVertexUsers, saveBdm, etc. remain exactly the same)
      async init() {
         this.isLoading = true;
         try {
            await Promise.all([this.getBdmList(), this.getVertexUsers()]);
            this.filterAvailableUsers();
         } catch (error) {
            console.error("Data initialization failed:", error);
         } finally {
            this.isLoading = false;
         }
      },
      async getVertexUsers() {
         let users = this.$store.state.auth.usersWithVertexRoles;
         if (!users || !users.length) {
            try {
               const response = await this.$axios.get("/auth/users/super-admin");
               users = response.data.super_admin_users || [];
               this.$store.commit("auth/setUsersWithVertexRoles", users);
            } catch (error) {
               console.error("Error fetching super admin users in dashboard:", error);
               users = [];
            }
         }
          this.allVertexUsers = users.map(item => ({
             id: item.id,
             first_name: item.first_name,
             last_name: item.last_name,
             email: item.email,
             fullName: `${item.first_name} ${item.last_name}`
          }));
      },
      async getBdmList() {
         try {
            const response = await this.$axios.get('/vertex-bdm');
            if (response.data.success) {
               this.allBdms = response.data.bdms || [];
               this.processClientSideData();
            } else {
               this.$toast.error(response.data.message || "Failed to fetch BDM list.");
            }
         } catch (error) {
            console.error("Failed to fetch BDM list:", error);
            this.$toast.error("An error occurred while fetching the BDM list.");
            throw error;
         }
      },
      filterAvailableUsers() {
         const bdmFullNames = new Set(this.allBdms.map(bdm => `${bdm.first_name || ''} ${bdm.last_name || ''}`));
         this.vertexUsers = this.allVertexUsers.filter(user => !bdmFullNames.has(user.fullName));
      },
      processClientSideData() {
         let processedList = [...this.allBdms];
         if (this.search) {
            const searchTerm = this.search.toLowerCase();
            processedList = processedList.filter(bdm => {
               const firstName = bdm.first_name || '';
               const lastName = bdm.last_name || '';
               const fullName = `${firstName} ${lastName}`.toLowerCase();
               return fullName.includes(searchTerm) ||
                  (bdm.email && bdm.email.toLowerCase().includes(searchTerm)) ||
                  (bdm.mobile && bdm.mobile.toLowerCase().includes(searchTerm));
            });
         }
         processedList.sort((a, b) => {
            switch (this.sortBy) {
               case 'name_asc': {
                  const aLast = (a.last_name || '').toLowerCase();
                  const bLast = (b.last_name || '').toLowerCase();
                  const aFirst = (a.first_name || '').toLowerCase();
                  const bFirst = (b.first_name || '').toLowerCase();
                  if (aLast < bLast) return -1;
                  if (aLast > bLast) return 1;
                  if (aFirst < bFirst) return -1;
                  if (aFirst > bFirst) return 1;
                  return 0;
               }
               case 'date_desc':
                  return new Date(b.date_created) - new Date(a.date_created);
               case 'date_asc':
                  return new Date(a.date_created) - new Date(b.date_created);
               default:
                  return 0;
            }
         });
         this.pagination.totalItems = processedList.length;
         this.pagination.totalPages = Math.ceil(processedList.length / this.pagination.pageSize);
         if (this.pagination.page > this.pagination.totalPages && this.pagination.totalPages > 0) {
            this.pagination.page = this.pagination.totalPages;
         }
         const start = (this.pagination.page - 1) * this.pagination.pageSize;
         const end = start + this.pagination.pageSize;
         this.bdmList = processedList.slice(start, end);
      },
      onSearch() {
         this.pagination.page = 1;
         this.debounceFn();
      },
      openAddModal() {
         this.isEditing = false;
         this.filterAvailableUsers();
         this.currentBdm = {
            id: null,
            userId: "",
            countryCode: '+61',
            localMobile: "",
            email: "",
            calendlyLink: "",
            onBehalfOfUserId: ""
         };
         this.isDropdownOpen = false;
         this.isModalOpen = true;
      },
      openEditModal(bdm) {
         this.isEditing = true;
         this.filterAvailableUsers();
         const bdmFullName = `${bdm.first_name || ''} ${bdm.last_name || ''}`;
         const userToEdit = this.allVertexUsers.find(u => u.fullName === bdmFullName);
         
         if (userToEdit && !this.vertexUsers.some(u => u.id === userToEdit.id)) {
            this.vertexUsers.push(userToEdit);
            sortAlphabetically(this.vertexUsers, item => item.fullName);
         }
         
         const { countryCode, localMobile } = this.parseMobileNumber(bdm.mobile);
         this.currentBdm = {
            id: bdm.id,
            userId: userToEdit ? userToEdit.id : "",
            countryCode: countryCode,
            localMobile: localMobile,
            email: bdm.email,
            calendlyLink: bdm.calendar_link,
            onBehalfOfUserId: bdm.on_behalf_of_auth_user_id || "",
         };
         this.isDropdownOpen = false;
         this.isModalOpen = true;
      },
      parseMobileNumber(mobileNumber) {
         const defaultCode = '+61';
         if (!mobileNumber) {
            return { countryCode: defaultCode, localMobile: '' };
         }
         
         const sortedCodes = [...this.countryCodes].sort((a, b) => b.code.length - a.code.length);

         for (const country of sortedCodes) {
            if (mobileNumber.startsWith(country.code)) {
               return {
                  countryCode: country.code,
                  localMobile: mobileNumber.substring(country.code.length)
               };
            }
         }
         return { countryCode: defaultCode, localMobile: mobileNumber };
      },
      async saveBdm() {
         const selectedUser = this.allVertexUsers.find(u => u.id === this.currentBdm.userId);
         if (!selectedUser) {
            this.$toast.error("Please select a user.");
            return;
         }
         const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
         if (!emailRegex.test(this.currentBdm.email)) {
            this.$toast.error("Please enter a valid email address.");
            return;
         }
         const mobileRegex = /^\d+$/;
         if (!this.currentBdm.localMobile || !mobileRegex.test(this.currentBdm.localMobile)) {
            this.$toast.error("Please enter a valid mobile number (digits only).");
            return;
         }

         const fullMobileNumber = `${this.currentBdm.countryCode}${this.currentBdm.localMobile}`;
         const payload = {
            id: this.currentBdm.id,
            auth_user_id: selectedUser.id,
            first_name: selectedUser.first_name,
            last_name: selectedUser.last_name,
            mobile: fullMobileNumber,
            email: this.currentBdm.email,
            calendar_link: this.currentBdm.calendlyLink,
            on_behalf_of_auth_user_id: 0,
         };
         
         try {
            const response = await this.$axios.post('/vertex-bdm', payload);
            if (response.data.success) {
               this.$toast.success(response.data.message || `BDM ${this.isEditing ? 'updated' : 'created'} successfully!`);
               await this.getBdmList();
               this.filterAvailableUsers();
               this.isModalOpen = false;
            } else {
               this.$toast.error(response.data.message || "Failed to save BDM.");
            }
         } catch (error) {
            console.error("Failed to save BDM:", error);
            this.$toast.error("An error occurred while saving the BDM.");
         }
      },
      deleteBdm(bdm) {
         this.pendingDeleteId = bdm.id || bdm.db_id;
         this.confirmModalTitle = 'Delete BDM';
         this.confirmModalMessage = 'Are you sure you want to delete this BDM?';
         this.showConfirmModal = true;
      },
      async onConfirmAction() {
         const id = this.pendingDeleteId;
         this.pendingDeleteId = null;
         if (!id) return;
         try {
            const response = await this.$axios.delete(`/vertex-bdm/${id}`);
            if (response.data.success) {
               this.$toast.success(response.data.message || "BDM deleted successfully.");
               await this.getBdmList();
               this.filterAvailableUsers();
            } else {
               this.$toast.error(response.data.message || "Failed to delete BDM.");
            }
         } catch (error) {
            console.error(`Failed to delete BDM with ID ${id}:`, error);
            this.$toast.error("An error occurred while deleting the BDM.");
         }
      },
      previousPage() { if (this.pagination.page > 1) { this.pagination.page--; } },
      nextPage() { if (this.pagination.page < this.pagination.totalPages) { this.pagination.page++; } },
      isUserDeleted(bdm) {
         if (!this.allVertexUsers || !this.allVertexUsers.length) return false;
         return !this.allVertexUsers.some(user => user.id === bdm.auth_user_id);
      },
      getOnBehalfOfName(userId) {
         if (!userId) return '—';
         const user = this.allVertexUsers.find(u => u.id === userId);
         return user ? user.fullName : 'Unknown User';
      },
      async setDefaultBdm(bdm) {
         if (bdm.is_default) return;
         try {
            const bdmId = bdm.id || bdm.db_id;
            const response = await this.$axios.put(`/vertex-bdm/${bdmId}/set-default`);
            if (response.data.success) {
               this.$toast.success(response.data.message || `${bdm.first_name} ${bdm.last_name} set as Default BDM.`);
               await this.getBdmList();
            } else {
               this.$toast.error(response.data.message || "Failed to set default BDM.");
            }
         } catch (error) {
            console.error("Failed to set default BDM:", error);
            const msg = error.response && error.response.data && error.response.data.message 
               ? error.response.data.message 
               : "An error occurred while setting default BDM.";
            this.$toast.error(msg);
         }
      },
   }
};
</script>

<style scoped>
/* STYLES FOR OUR NEW MANUAL DROPDOWN */
.custom-dropdown {
   position: relative;
   width: 160px;
   flex: none; /* Prevents it from shrinking/growing in the input-group */
}

.dropdown-selected {
   display: flex;
   align-items: center;
   height: 100%;
   padding: 0.375rem 0.75rem;
   border: 1px solid #dee2e6;
   border-radius: 8px 0 0 8px; /* Match Bootstrap's input-group style */
   background-color: #fff;
   cursor: pointer;
   font-size: 1rem;
   user-select: none; /* Prevents text selection on click */
}

.dropdown-list {
   position: absolute;
   top: 105%; /* Position it just below the selected button */
   left: 0;
   width: 300px; /* Make the list wider to show full country names */
   max-height: 240px;
   overflow-y: auto;
   margin: 0;
   padding: 0;
   list-style: none;
   background-color: #fff;
   border: 1px solid #dee2e6;
   border-radius: 8px;
   box-shadow: 0 4px 8px rgba(0,0,0,0.1);
   z-index: 1060; /* Make sure it appears above other modal content */
}

.dropdown-list li {
   display: flex;
   align-items: center;
   padding: 0.5rem 1rem;
   cursor: pointer;
}

.dropdown-list li:hover {
   background-color: #f1f3f5;
}

/* --- General & Table Styles --- */
.mainTable td,
.mainTable th {
   text-align: left;
   vertical-align: middle;
   padding: 12px 8px;
}

.mainTable th {
   font-weight: 600;
}

/* ... (All other scoped styles remain the same) ... */
.filterTitle {
   font-weight: 500;
   color: #435666;
}

.filterOptions {
   font-weight: 500;
   color: #00A0C1;
}

.bordered-select {
   border: 1px solid #dee2e6 !important;
   border-radius: 8px !important;
   padding-top: 0.25rem;
   padding-bottom: 0.25rem;
   padding-left: 0.75rem;
   width: auto;
}

.pagination {
   display: inline-flex;
   align-items: center;
   border: 1px solid #dee2e6;
   border-radius: 8px;
   background-color: #fff;
   overflow: hidden;
}

.pagination-label {
   font-size: 0.9rem;
   color: #6c757d;
   font-weight: 500;
   padding: 0 8px;
   white-space: nowrap;
}

.pagination-label strong {
   color: #343a40;
}

.pagination-arrow {
   background-color: transparent;
   border: none;
   cursor: pointer;
   color: #00A0C1;
   font-size: 1rem;
   padding: 8px 12px;
   transition: background-color 0.2s ease;
}

.pagination-arrow:hover:not(:disabled) {
   background-color: #f1f3f5;
}

.pagination-arrow:disabled {
   color: #ced4da;
   cursor: not-allowed;
}

.action-cell {
   text-align: right;
}

.action-btn-circle {
   width: 36px;
   height: 36px;
   border-radius: 50% !important;
   border: none;
   cursor: pointer;
   font-size: 15px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
   text-decoration: none;
   position: relative;
   overflow: hidden;
}

.action-btn-circle:hover:not(:disabled) {
   transform: translateY(-1px);
}

.action-btn-circle:active:not(:disabled) {
   transform: translateY(0);
}

/* Default Star Buttons */
.action-btn-circle--default-active {
   background-color: #f0fdf4;
   color: #16a34a;
}

.action-btn-circle--default-active:hover:not(:disabled) {
   background-color: #16a34a;
   color: #ffffff;
   box-shadow: 0 4px 12px rgba(22, 163, 74, 0.25);
}

.action-btn-circle--default {
   background-color: #f8fafc;
   color: #94a3b8;
}

.action-btn-circle--default:hover:not(:disabled) {
   background-color: #16a34a;
   color: #ffffff;
   box-shadow: 0 4px 12px rgba(22, 163, 74, 0.25);
}

/* Edit Button */
.action-btn-circle--edit {
   background-color: #e6f7fa;
   color: #00A0C1;
}

.action-btn-circle--edit:hover:not(:disabled) {
   background-color: #00A0C1;
   color: #ffffff;
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}

/* Delete Button */
.action-btn-circle--delete {
   background-color: #fef2f2;
   color: #ef4444;
}

.action-btn-circle--delete:hover:not(:disabled) {
   background-color: #ef4444;
   color: #ffffff;
   box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
}

.modal-backdrop-custom {
   position: fixed;
   top: 0;
   left: 0;
   width: 100vw;
   height: 100vh;
   background-color: rgba(0, 0, 0, 0.5);
   display: flex;
   justify-content: center;
   align-items: center;
   z-index: 1050;
}

.modal-content-custom {
   position: relative;
   background-color: white;
   padding: 2.5rem 2rem 2rem 2rem;
   border-radius: 16px;
   width: 100%;
   max-width: 450px;
   box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-title-custom {
   font-weight: bold;
   font-size: 1.25rem;
   text-align: left;
   margin-bottom: 0;
}

.modal-content-custom hr {
   margin-top: 1rem;
   margin-bottom: 1.5rem;
}

.close-button-custom {
   position: absolute;
   top: 1rem;
   right: 1rem;
   font-size: 2rem;
   font-weight: bold;
   line-height: 1;
   color: #888;
   background: none;
   border: none;
   cursor: pointer;
}

.close-button-custom:hover {
   color: #000;
}

.modal-form-custom .form-label {
   font-weight: 500;
   color: #333;
}

.add-button-custom {
   padding: 10px 40px;
   font-size: 1rem;
   font-weight: bold;
   border-radius: 8px;
}

.badge-deleted {
   background-color: #fff1f2;
   color: #e11d48;
   border: 1px solid #ffe4e6;
   padding: 2px 8px;
   border-radius: 9999px;
   font-weight: 500;
   font-size: 11px;
   display: inline-flex;
   align-items: center;
}
.badge-orphaned {
   background-color: #fef3c7;
   color: #d97706;
   border: 1px solid #fde68a;
   padding: 2px 8px;
   border-radius: 9999px;
   font-weight: 500;
   font-size: 11px;
   display: inline-flex;
   align-items: center;
}
.text-muted {
   color: #888888;
}
.row-deleted {
   background-color: #fafafa;
}

/* Page Header Styles */
.ms-page-header {
   margin-bottom: 24px;
   padding: 20px 24px;
   background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
   border: 1px solid rgba(0, 130, 152, 0.15);
   border-left: 5px solid #008298;
   border-radius: 12px;
   box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}

.ms-page-header__content {
   display: flex;
   align-items: center;
   gap: 16px;
}

.ms-page-header__icon {
   width: 44px;
   height: 44px;
   border-radius: 10px;
   background: rgba(0, 130, 152, 0.08);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.15rem;
   color: #008298;
}

.ms-page-header__title {
   font-size: 1.45rem;
   font-weight: 800;
   color: #00363f;
   margin: 0;
   letter-spacing: -0.3px;
}

.ms-page-header__breadcrumb {
   font-size: 0.76rem;
   color: #557d84;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.8px;
   margin-bottom: 4px;
}

.ms-breadcrumb-sep {
   margin: 0 6px;
   color: rgba(0, 130, 152, 0.3);
}

.default-bdm-pill {
   background: #f6ffed;
   border: 1px solid #b7eb8f;
   color: #276727;
   padding: 6px 16px;
   border-radius: 20px;
   font-size: 0.85rem;
   font-weight: 500;
   display: inline-flex;
   align-items: center;
}

.badge-default-bdm {
   background-color: #f6ffed;
   color: #276727;
   border: 1px solid #b7eb8f;
   padding: 2px 8px;
   border-radius: 9999px;
   font-weight: 600;
   font-size: 11px;
   display: inline-flex;
   align-items: center;
}
</style>